import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  MapPin, Globe, Upload, Plus, Trash2, Ticket, Users, Check,
  Copy, Share2, ScanLine, KeyRound, Download, Loader2, X, ExternalLink,
} from "lucide-react";
import OrganizerSidebar from "./OrganizerSidebar";
import OrganizerMobileNavDropdown from "./OrganizerMobileNavDropdown";
import { InstaPassLogo } from "../layout/Components/InstaPassLogo";
import { publishEvent } from "./createEventPublish";
import { CREATE_EVENT_CATEGORIES } from "./createEventCategories";
import { COVER_THEMES, findTheme, coverDateLabel, renderCoverFile } from "./coverThemes";
import { renderQrSvg, downloadPng } from "../QRStudio/qrRender";

/* ═══════════════════════════════════════════════════════════════
   CREATE EVENT — one screen. The page you're editing is the page
   guests see. Only a name is required to start; publish is free.
   ═══════════════════════════════════════════════════════════════ */

const DRAFT_KEY = "instapass_quick_event_draft";
const SITE = "https://instapass.shop";

function nextSaturday() {
  const d = new Date();
  d.setDate(d.getDate() + ((6 - d.getDay() + 7) % 7 || 7));
  // Local calendar date (toISOString would shift to UTC and can land on Sunday)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const newTier = (over = {}) => ({
  id: Date.now() + Math.floor(Math.random() * 1000),
  ticketName: "General admission",
  price: "",
  quantity: "",
  serviceFee: "pass-to-buyer",
  ...over,
});

const EMPTY = {
  eventTitle: "",
  coverTheme: "night",
  startDate: nextSaturday(),
  startTime: "20:00",
  endDate: "",
  endTime: "23:00",
  eventFormat: "physical",
  venueName: "",
  venueAddress: "",
  venueCity: "",
  venueState: "",
  venueZip: "",
  eventLink: "",
  eventDescription: "",
  category: "",
  entryType: "rsvp", // rsvp | tickets
  capacity: "",
  maxPlusOnes: "1",
  tiers: [newTier()],
  eventVisibility: "public",
};

function loadDraft() {
  try {
    const d = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null");
    return d ? { ...EMPTY, ...d } : EMPTY;
  } catch (_) {
    return EMPTY;
  }
}

const input =
  "w-full rounded-xl border border-white/10 bg-[#0E1626] px-4 py-3 text-[15px] text-white placeholder-white/35 outline-none focus:border-[#E52324]/70 transition";
const label = "mb-1.5 block text-[12px] font-semibold text-white/60";

function Field({ title, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className={label}>{title}</span>
      {children}
    </label>
  );
}

function validate(f) {
  if (!f.eventTitle.trim()) return "Give your event a name.";
  if (!f.startDate || !f.startTime) return "Pick a date and start time.";
  const start = new Date(`${f.startDate}T${f.startTime}:00`);
  if (Number.isNaN(start.getTime()) || start < new Date()) return "The start time is in the past.";
  if (f.eventFormat === "online") {
    if (!/^https?:\/\/\S+\.\S+/.test(f.eventLink.trim())) return "Add the link guests will use to join (https://…).";
  } else if (!f.venueAddress.trim() || !f.venueCity.trim()) {
    return "Add the street address and city.";
  }
  if (f.entryType === "tickets") {
    for (const t of f.tiers) {
      if (!t.ticketName.trim()) return "Name each ticket type.";
      const p = parseFloat(t.price);
      if (!(p > 0)) return `Set a price for “${t.ticketName}”, or switch to Free RSVP.`;
      if (!(parseInt(t.quantity, 10) > 0)) return `Set how many “${t.ticketName}” tickets are for sale.`;
    }
  }
  return "";
}

// End time is on the next day when it's earlier than the start (9 PM → 2 AM).
function endDateFor(f) {
  if (!f.endTime) return f.startDate;
  return f.endTime <= f.startTime
    ? new Date(new Date(`${f.startDate}T12:00:00`).getTime() + 86400000).toISOString().slice(0, 10)
    : f.startDate;
}

export default function QuickCreateEvent() {
  const navigate = useNavigate();
  const [f, setF] = useState(loadDraft);
  const [photo, setPhoto] = useState(null); // { file, url }
  const [busy, setBusy] = useState(false);
  const [published, setPublished] = useState(null); // { id, title }
  const [payoutsNeeded, setPayoutsNeeded] = useState(false);
  const fileRef = useRef(null);
  const doneRef = useRef(false); // stop autosave once published
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e }));
  const theme = findTheme(f.coverTheme);

  useEffect(() => {
    if (doneRef.current) return undefined;
    const t = setTimeout(() => {
      if (doneRef.current) return;
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify(f));
      } catch (_) {}
    }, 500);
    return () => clearTimeout(t);
  }, [f]);

  useEffect(() => () => photo?.url && URL.revokeObjectURL(photo.url), [photo]);

  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\//.test(file.type)) return toast.error("Choose an image (JPG, PNG, WebP or GIF).");
    if (file.size > 10 * 1024 * 1024) return toast.error("That image is over 10 MB.");
    setPhoto({ file, url: URL.createObjectURL(file) });
  };

  const setTier = (i, k, v) =>
    setF((p) => ({ ...p, tiers: p.tiers.map((t, j) => (j === i ? { ...t, [k]: v } : t)) }));

  const publish = async () => {
    const problem = validate(f);
    if (problem) return toast.error(problem);
    setBusy(true);
    try {
      const image = photo?.file || (await renderCoverFile({ themeId: f.coverTheme, title: f.eventTitle, startDate: f.startDate, startTime: f.startTime }));
      const tickets =
        f.entryType === "rsvp"
          ? [{ id: Date.now(), ticketName: "RSVP", price: "0", quantity: String(parseInt(f.capacity, 10) || 0), serviceFee: "absorb" }]
          : f.tiers.map((t) => ({ ...t, price: String(parseFloat(t.price)), quantity: String(parseInt(t.quantity, 10)) }));
      const result = await publishEvent({
        ...f,
        endDate: endDateFor(f),
        venueName: f.eventFormat === "online" ? "Online" : f.venueName || f.venueAddress,
        venueAddress: f.eventFormat === "online" ? "" : f.venueAddress,
        maxPlusOnes: f.entryType === "rsvp" ? f.maxPlusOnes : 0,
        tickets,
        eventImage: image,
      });
      const id = result?.event?._id;
      if (!id) throw new Error("Published, but we couldn't open it. Find it in My Events.");
      doneRef.current = true;
      try {
        localStorage.removeItem(DRAFT_KEY);
      } catch (_) {}
      setPublished({ id, title: f.eventTitle.trim() });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      if (err.code === "PAYOUTS_REQUIRED") {
        setPayoutsNeeded(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        toast.error(err.message || "Couldn't publish. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  const startOver = () => {
    doneRef.current = false;
    setF({ ...EMPTY, tiers: [newTier()] });
    setPhoto(null);
    setPublished(null);
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#060D1B] text-white" style={{ fontFamily: "'Outfit', 'Inter', sans-serif" }}>
      <OrganizerSidebar />
      <div className="ml-0 lg:ml-[260px]">
        <header className="sticky top-0 z-30 flex flex-wrap items-center justify-between gap-3 border-b border-white/[0.06] bg-[#060D1B]/95 px-4 py-3 backdrop-blur-lg sm:px-8">
          <div className="flex min-w-0 items-center gap-3">
            <div className="lg:hidden"><OrganizerMobileNavDropdown /></div>
            <Link to="/" className="shrink-0" aria-label="InstaPass home"><InstaPassLogo size="sm" /></Link>
            <span className="hidden truncate text-[13px] text-white/55 sm:inline">
              {published ? "Event published" : "New event · draft saves automatically"}
            </span>
          </div>
          {!published && (
            <button
              type="button"
              onClick={publish}
              disabled={busy}
              className="flex h-11 items-center gap-2 rounded-xl bg-[#CC1F21] px-6 text-[15px] font-bold text-white transition hover:brightness-110 disabled:opacity-60"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              {busy ? "Publishing…" : "Publish — free"}
            </button>
          )}
        </header>

        {payoutsNeeded && !published && (
          <div role="alert" className="mx-auto mt-6 flex max-w-[1120px] flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-400/30 bg-amber-400/10 px-5 py-4 text-[14px] text-white sm:mx-8">
            <span>
              <strong className="block text-[15px]">Set up payouts to sell tickets</strong>
              Your draft is saved. Connect your bank once (about 5 minutes), then come back and publish. Free RSVP events can publish now.
            </span>
            <span className="flex gap-2">
              <Link to="/organizer/payouts?next=%2Fcreate-event%2Fform" className="flex h-11 items-center rounded-xl bg-white px-5 text-[14px] font-bold text-[#0B1120]">
                Set up payouts
              </Link>
              <button type="button" onClick={() => setPayoutsNeeded(false)} className="h-11 rounded-xl border border-white/20 px-4 text-[14px] text-white/80">
                Dismiss
              </button>
            </span>
          </div>
        )}
        {published ? (
          <PublishedPanel event={published} theme={theme} onNew={startOver} navigate={navigate} />
        ) : (
          <main className="mx-auto flex max-w-[1120px] flex-wrap items-start gap-8 px-4 py-8 sm:px-8 lg:gap-10">
            {/* ── Cover ── */}
            <section aria-label="Cover" className="w-full max-w-[460px] flex-[1_1_340px] lg:sticky lg:top-[88px]">
              <div className="relative aspect-square overflow-hidden rounded-[22px]" style={{ background: theme.bg }}>
                {photo ? (
                  <img src={photo.url} alt="Event cover" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full flex-col justify-between p-7">
                    <div className="flex items-start justify-between">
                      <span className="text-[13px] font-extrabold tracking-[0.14em]" style={{ color: theme.ink }}>
                        {coverDateLabel(f.startDate, f.startTime)}
                      </span>
                      <span className="h-16 w-16 rounded-full" style={{ background: theme.accent }} />
                    </div>
                    <div className="break-words text-[clamp(34px,5vw,54px)] font-extrabold leading-[0.95] tracking-[-0.03em]" style={{ color: theme.ink }}>
                      {f.eventTitle.trim() || "Your event"}
                    </div>
                    <div className="h-2.5 w-28 rounded" style={{ background: theme.accent }} />
                  </div>
                )}
                {photo && (
                  <button
                    type="button"
                    onClick={() => setPhoto(null)}
                    className="absolute right-3 top-3 flex h-9 items-center gap-1.5 rounded-full bg-black/60 px-3 text-[13px] font-semibold text-white"
                  >
                    <X className="h-4 w-4" /> Use a theme instead
                  </button>
                )}
              </div>
              <div className="mt-4 flex flex-wrap gap-2.5">
                {COVER_THEMES.map((t) => {
                  const on = !photo && t.id === f.coverTheme;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => { setPhoto(null); setF((p) => ({ ...p, coverTheme: t.id })); }}
                      aria-label={`Cover theme ${t.name}`}
                      aria-pressed={on}
                      className="flex flex-col items-center gap-1.5 text-[12px] text-white/75"
                    >
                      <span className="h-12 w-12 rounded-xl" style={{ background: t.bg, boxShadow: on ? "inset 0 0 0 3px #fff" : "inset 0 0 0 1px rgba(255,255,255,.2)" }} />
                      {t.name}
                    </button>
                  );
                })}
                <button type="button" onClick={() => fileRef.current?.click()} className="flex flex-col items-center gap-1.5 text-[12px] text-white/75">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-dashed border-white/35"><Upload className="h-5 w-5" /></span>
                  Photo
                </button>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPhoto} />
              </div>
            </section>

            {/* ── Details ── */}
            <section aria-label="Event details" className="flex min-w-0 flex-[999_1_520px] flex-col gap-6">
              <Field title="Event name">
                <input
                  value={f.eventTitle}
                  onChange={set("eventTitle")}
                  maxLength={90}
                  placeholder="Name your event"
                  autoFocus
                  className="w-full border-0 border-b-2 border-white/15 bg-transparent pb-2 text-[clamp(28px,4vw,42px)] font-extrabold tracking-[-0.02em] text-white placeholder-white/25 outline-none focus:border-[#E52324]"
                />
              </Field>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <Field title="Date"><input type="date" value={f.startDate} onChange={set("startDate")} className={input} /></Field>
                <Field title="Starts"><input type="time" value={f.startTime} onChange={set("startTime")} className={input} /></Field>
                <Field title="Ends"><input type="time" value={f.endTime} onChange={set("endTime")} className={input} /></Field>
              </div>

              <div className="flex flex-col gap-3">
                <div role="radiogroup" aria-label="Where" className="flex gap-2">
                  {[["physical", "In person", MapPin], ["online", "Online", Globe]].map(([v, l, Icon]) => (
                    <button
                      key={v}
                      type="button"
                      role="radio"
                      aria-checked={f.eventFormat === v}
                      onClick={() => setF((p) => ({ ...p, eventFormat: v }))}
                      className={`flex h-10 items-center gap-2 rounded-full border px-4 text-[14px] font-semibold transition ${f.eventFormat === v ? "border-white bg-white text-[#060D1B]" : "border-white/15 text-white/70 hover:text-white"}`}
                    >
                      <Icon className="h-4 w-4" /> {l}
                    </button>
                  ))}
                </div>
                {f.eventFormat === "online" ? (
                  <Field title="Join link"><input value={f.eventLink} onChange={set("eventLink")} placeholder="https://zoom.us/j/…" inputMode="url" className={input} /></Field>
                ) : (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-6">
                    <Field title="Venue name (optional)" className="sm:col-span-6"><input value={f.venueName} onChange={set("venueName")} placeholder="The Roof" className={input} /></Field>
                    <Field title="Street address" className="sm:col-span-6"><input value={f.venueAddress} onChange={set("venueAddress")} placeholder="123 Main St" autoComplete="street-address" className={input} /></Field>
                    <Field title="City" className="sm:col-span-3"><input value={f.venueCity} onChange={set("venueCity")} autoComplete="address-level2" className={input} /></Field>
                    <Field title="State" className="sm:col-span-1"><input value={f.venueState} onChange={set("venueState")} maxLength={2} placeholder="CA" autoComplete="address-level1" className={`${input} uppercase`} /></Field>
                    <Field title="ZIP" className="sm:col-span-2"><input value={f.venueZip} onChange={set("venueZip")} inputMode="numeric" autoComplete="postal-code" className={input} /></Field>
                  </div>
                )}
              </div>

              <Field title="What's the plan?">
                <textarea value={f.eventDescription} onChange={set("eventDescription")} rows={3} placeholder="Dress code, lineup, what to bring…" className={`${input} resize-y leading-relaxed`} />
              </Field>

              <div>
                <span className={label}>Category</span>
                <div className="flex flex-wrap gap-2">
                  {CREATE_EVENT_CATEGORIES.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      aria-pressed={f.category === c.id}
                      onClick={() => setF((p) => ({ ...p, category: p.category === c.id ? "" : c.id }))}
                      className={`h-9 rounded-full border px-3.5 text-[13px] font-semibold transition ${f.category === c.id ? "border-white bg-white text-[#060D1B]" : "border-white/15 text-white/70 hover:text-white"}`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* ── Entry ── */}
              <div className="flex flex-col gap-3">
                <span className={label}>How do guests get in?</span>
                <div role="radiogroup" aria-label="Entry type" className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {[
                    ["rsvp", "Free RSVP", "Guests RSVP with name + email and get a QR pass. No account needed.", Users],
                    ["tickets", "Sell tickets", "Paid QR tickets. Payout 3 days after the event.", Ticket],
                  ].map(([v, t, d, Icon]) => {
                    const on = f.entryType === v;
                    return (
                      <button
                        key={v}
                        type="button"
                        role="radio"
                        aria-checked={on}
                        onClick={() => setF((p) => ({ ...p, entryType: v }))}
                        className={`flex min-h-[84px] flex-col gap-1 rounded-2xl border-2 p-4 text-left transition ${on ? "border-[#E52324] bg-[#E52324]/10" : "border-white/10 bg-[#0E1626] hover:border-white/25"}`}
                      >
                        <span className="flex items-center gap-2 text-[16px] font-bold"><Icon className="h-4 w-4 text-[#FF8A8A]" />{t}</span>
                        <span className="text-[13px] leading-snug text-white/65">{d}</span>
                      </button>
                    );
                  })}
                </div>

                {f.entryType === "rsvp" ? (
                  <div className="grid grid-cols-1 gap-3 rounded-2xl border border-white/10 bg-[#0E1626] p-4 sm:grid-cols-2">
                    <Field title="Capacity (blank = no limit)"><input value={f.capacity} onChange={set("capacity")} inputMode="numeric" placeholder="No limit" className={input} /></Field>
                    <Field title="Each guest can bring">
                      <select value={f.maxPlusOnes} onChange={set("maxPlusOnes")} className={input}>
                        <option value="0">No plus-ones</option>
                        <option value="1">+1</option>
                        <option value="2">Up to +2</option>
                        <option value="4">Up to +4</option>
                      </select>
                    </Field>
                  </div>
                ) : (
                  <div className="flex flex-col gap-3 rounded-2xl border border-white/10 bg-[#0E1626] p-4">
                    {f.tiers.map((t, i) => (
                      <div key={t.id} className="grid grid-cols-2 gap-3 border-b border-white/[0.06] pb-3 last:border-0 last:pb-0 sm:grid-cols-[2fr_1fr_1fr_auto]">
                        <Field title="Ticket name" className="col-span-2 sm:col-span-1"><input value={t.ticketName} onChange={(e) => setTier(i, "ticketName", e.target.value)} className={input} /></Field>
                        <Field title="Price ($)"><input value={t.price} onChange={(e) => setTier(i, "price", e.target.value)} inputMode="decimal" placeholder="25" className={input} /></Field>
                        <Field title="Quantity"><input value={t.quantity} onChange={(e) => setTier(i, "quantity", e.target.value)} inputMode="numeric" placeholder="100" className={input} /></Field>
                        <div className="col-span-2 flex items-end justify-between gap-3 sm:col-span-1">
                          <label className="flex min-h-[44px] items-center gap-2 text-[13px] text-white/75">
                            <input type="checkbox" checked={t.serviceFee === "absorb"} onChange={(e) => setTier(i, "serviceFee", e.target.checked ? "absorb" : "pass-to-buyer")} className="accent-[#E52324]" />
                            I'll cover fees
                          </label>
                          {f.tiers.length > 1 && (
                            <button type="button" aria-label={`Remove ${t.ticketName || "ticket"}`} onClick={() => setF((p) => ({ ...p, tiers: p.tiers.filter((_, j) => j !== i) }))} className="flex h-11 w-11 items-center justify-center rounded-xl text-white/50 hover:bg-white/5 hover:text-white">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <button type="button" onClick={() => setF((p) => ({ ...p, tiers: [...p.tiers, newTier({ ticketName: "VIP" })] }))} className="flex h-10 items-center gap-2 rounded-xl border border-white/15 px-4 text-[14px] font-semibold text-white/85 hover:bg-white/5">
                        <Plus className="h-4 w-4" /> Add ticket type
                      </button>
                      <p className="text-[12px] text-white/55">InstaPass fee: $1.49 per ticket + card processing. Buyers pay it unless you cover it.</p>
                    </div>
                  </div>
                )}
              </div>

              <label className="flex min-h-[44px] items-center gap-3 text-[14px] text-white/80">
                <input type="checkbox" checked={f.eventVisibility === "private"} onChange={(e) => setF((p) => ({ ...p, eventVisibility: e.target.checked ? "private" : "public" }))} className="h-4 w-4 accent-[#E52324]" />
                Private — only people with the link can find it
              </label>

              <button
                type="button"
                onClick={publish}
                disabled={busy}
                className="flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-[#CC1F21] text-[17px] font-bold text-white transition hover:brightness-110 disabled:opacity-60 sm:hidden"
              >
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Check className="h-5 w-5" />}
                {busy ? "Publishing…" : "Publish — free"}
              </button>
            </section>
          </main>
        )}
      </div>
    </div>
  );
}

function PublishedPanel({ event, onNew, navigate }) {
  const link = `${SITE}/tickets/group.details/${event.id}`;
  const qr = useMemo(() => {
    try {
      return renderQrSvg({ payload: link, fg: "#0B0F19", bg: "#FFFFFF", pattern: "rounded", corner: "rounded", logo: "instapass", label: "Scan for the event" });
    } catch (_) {
      return null;
    }
  }, [link]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link copied");
    } catch (_) {
      toast.error("Couldn't copy — select the link and copy it.");
    }
  };
  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: event.title, text: `You're invited: ${event.title}`, url: link });
      } catch (_) {}
    } else copy();
  };

  return (
    <main className="mx-auto flex max-w-[960px] flex-wrap items-start gap-8 px-4 py-10 sm:px-8">
      <section className="flex min-w-0 flex-[999_1_420px] flex-col gap-5">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-400"><Check className="h-6 w-6" /></span>
        <h1 className="text-[clamp(28px,4vw,40px)] font-extrabold leading-tight tracking-[-0.02em]">{event.title} is live</h1>
        <p className="text-[15px] text-white/70">Share the link to start getting guests. Everyone who RSVPs or buys gets a QR pass by email, and you scan it at the door.</p>

        <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-white/10 bg-[#0E1626] p-3">
          <span className="min-w-0 flex-1 truncate px-2 text-[14px] text-white/90">{link.replace("https://", "")}</span>
          <button type="button" onClick={copy} className="flex h-10 items-center gap-2 rounded-xl border border-white/15 px-4 text-[14px] font-semibold hover:bg-white/5"><Copy className="h-4 w-4" /> Copy</button>
          <button type="button" onClick={share} className="flex h-10 items-center gap-2 rounded-xl bg-white px-4 text-[14px] font-bold text-[#060D1B]"><Share2 className="h-4 w-4" /> Share</button>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <button type="button" onClick={() => navigate(`/organizer/check-in?event=${event.id}&tab=scan`)} className="flex min-h-[64px] items-center gap-3 rounded-2xl border border-white/10 bg-[#0E1626] p-4 text-left hover:border-white/25">
            <ScanLine className="h-6 w-6 shrink-0 text-[#FF8A8A]" />
            <span><strong className="block text-[15px]">Open door scanner</strong><span className="text-[13px] text-white/60">Check guests in with your phone camera</span></span>
          </button>
          <button type="button" onClick={() => navigate(`/organizer/check-in?event=${event.id}&tab=doors`)} className="flex min-h-[64px] items-center gap-3 rounded-2xl border border-white/10 bg-[#0E1626] p-4 text-left hover:border-white/25">
            <KeyRound className="h-6 w-6 shrink-0 text-[#FF8A8A]" />
            <span><strong className="block text-[15px]">Door codes for staff</strong><span className="text-[13px] text-white/60">Let staff scan at instapass.shop/door, no account</span></span>
          </button>
          <button type="button" onClick={() => navigate(`/organizer/check-in?event=${event.id}&tab=attendees`)} className="flex min-h-[64px] items-center gap-3 rounded-2xl border border-white/10 bg-[#0E1626] p-4 text-left hover:border-white/25">
            <Users className="h-6 w-6 shrink-0 text-[#FF8A8A]" />
            <span><strong className="block text-[15px]">Guest list</strong><span className="text-[13px] text-white/60">Who's coming, live, with CSV export</span></span>
          </button>
          <a href={link} target="_blank" rel="noreferrer" className="flex min-h-[64px] items-center gap-3 rounded-2xl border border-white/10 bg-[#0E1626] p-4 text-left text-white hover:border-white/25">
            <ExternalLink className="h-6 w-6 shrink-0 text-[#FF8A8A]" />
            <span><strong className="block text-[15px]">View event page</strong><span className="text-[13px] text-white/60">What your guests see</span></span>
          </a>
        </div>
        <button type="button" onClick={onNew} className="self-start text-[14px] font-semibold text-[#FF8A8A] hover:text-[#FFB3B3]">+ Create another event</button>
      </section>

      {qr && (
        <aside className="w-full max-w-[300px] flex-[1_1_240px] rounded-2xl border border-white/10 bg-[#0E1626] p-4">
          <div className="rounded-xl bg-white p-4 [&>svg]:h-auto [&>svg]:w-full" dangerouslySetInnerHTML={{ __html: qr.svg }} />
          <p className="mt-3 text-[13px] text-white/65">Event QR for flyers and posters — opens your event page.</p>
          <button type="button" onClick={() => downloadPng(qr.svg, qr.aspect, `instapass-event-${event.id}`)} className="mt-3 flex h-10 w-full items-center justify-center gap-2 rounded-xl border border-white/15 text-[14px] font-semibold hover:bg-white/5">
            <Download className="h-4 w-4" /> Download QR
          </button>
        </aside>
      )}
    </main>
  );
}
